import Contract from "slotworks/utils/contracts/contract";
import Layers from "slotworks/graphics/layers/layers";
import {WILD_COMBINATIONS} from "./sg-wilds-component";

export default class SGWildSpineAnimation {
    private animation: PIXI.spine.Spine;

    private expandAnimations: string[][] = [
        ["expanding-ra", "expanding-ra2", "expanding-ra3"],
        ["expanding-maat", "expanding-maat2", "expanding-maat3"],
        ["expanding-khnum", "expanding-khnum2", "expanding-khnum3"]
    ];

    private winAnimations: {[key: string]: string;} = {
        [WILD_COMBINATIONS.RA]: "win-long-ra",
        [WILD_COMBINATIONS.MAAT]: "win-long-maat",
        [WILD_COMBINATIONS.KHNUM]: "win-long-khunm",
        [WILD_COMBINATIONS.RA_MAAT]: "win-long-ra-maat",
        [WILD_COMBINATIONS.MAAT_KHNUM]: "win-long-maat-khnum",
        [WILD_COMBINATIONS.RA_MAAT_KHNUM]: "win-long-ra-maat-khnum",
    };

    private highlightAnimations: {[key: string]: string;} = {
        [WILD_COMBINATIONS.RA]: "win-short-ra",
        [WILD_COMBINATIONS.MAAT]: "win-short-maat2",
        [WILD_COMBINATIONS.KHNUM]: "win-short-khunm",
        [WILD_COMBINATIONS.RA_MAAT]: "win-long-ra-maat",
        [WILD_COMBINATIONS.MAAT_KHNUM]: "win-short-maat-khnum",
        [WILD_COMBINATIONS.RA_MAAT_KHNUM]: "win-long-ra-maat-khnum",
    };

    private bonusIntroAnimations: {[key: string]: string;} = {
        [WILD_COMBINATIONS.RA]: "bonus-trigger-ra",
        [WILD_COMBINATIONS.MAAT]: "bonus-trigger-maat",
        [WILD_COMBINATIONS.KHNUM]: "bonus-trigger-khnum",
        [WILD_COMBINATIONS.RA_MAAT]: "bonus-trigger-ra-maat",
        [WILD_COMBINATIONS.MAAT_KHNUM]: "bonus-trigger-maat-khnum",
        [WILD_COMBINATIONS.RA_MAAT_KHNUM]: "bonus-trigger-ra-maat-khnum",
    };


    private combineAnimations: {[key: string]: string;} = {
        [WILD_COMBINATIONS.RA + "+" + WILD_COMBINATIONS.MAAT]: "join-ra+maat",
        [WILD_COMBINATIONS.RA + "+" + WILD_COMBINATIONS.MAAT + "+" + WILD_COMBINATIONS.KHNUM]: "join-ra+maat+khnum",
        [WILD_COMBINATIONS.RA_MAAT + "+" + WILD_COMBINATIONS.KHNUM]: "join-ra+maat-to-triple",
        [WILD_COMBINATIONS.MAAT + "+" + WILD_COMBINATIONS.KHNUM]: "join-maat+khnum",
        [WILD_COMBINATIONS.RA + "+" + WILD_COMBINATIONS.MAAT_KHNUM]: "join-maat + khnum-to-triple",
    };

    constructor(index: number) {
        this.animation = Layers.SymbolAnimations.getSpine("wilds_spine_" + (index + 1));
        this.animation.visible = false;
    }

    public playExpandAnimation(reel: number, cell: number): Contract<void> {
        this.animation.visible = true;
        this.animation.autoUpdate = true;
        this.animation.state.clearListeners();
        this.animation.state.setAnimation(0, this.expandAnimations[reel - 1][cell], false);
        this.updateAnimationFrame();
        return new Contract<void>((resolve) => {
            this.animation.state.addListener({
                complete: () => {
                    this.animation.state.clearListeners();
                    resolve();
                }
            });
        });
    }

    public playCombineAnimation(combination: string[]): Contract<void> {
        this.animation.visible = true;
        this.animation.autoUpdate = true;
        this.animation.state.clearListeners();
        this.animation.state.setAnimation(0, this.combineAnimations[combination.join("+")], false);
        this.updateAnimationFrame();
        return new Contract<void>((resolve) => {
            this.animation.state.addListener({
                complete: () => {
                    this.animation.state.clearListeners();
                    resolve();
                }
            });
        });
    }

    public playBonusIntroAnimation(id: string): Contract<void> {
        this.animation.visible = true;
        this.animation.autoUpdate = true;
        this.animation.state.clearListeners();
        this.animation.state.setAnimation(0, this.bonusIntroAnimations[id], false);
        this.updateAnimationFrame();
        return new Contract<void>(resolve => {
            this.animation.state.addListener({
                complete: () => {
                    this.animation.state.clearListeners();
                    resolve(null);
                }
            });
        });
    }

    public playWinAnimation(id: string): Contract<void> {
        this.animation.visible = true;
        this.animation.autoUpdate = true;
        this.animation.state.clearListeners();
        this.animation.state.setAnimation(0, this.winAnimations[id], false);
        this.updateAnimationFrame();
        return new Contract<void>(resolve => {
            this.animation.state.addListener({
                complete: () => {
                    this.animation.state.clearListeners();
                    resolve(null);
                }
            });
        });
    }

    public playHighlightAnimation(id: string): Contract<void> {
        this.animation.visible = true;
        this.animation.autoUpdate = true;
        this.animation.state.clearListeners();
        this.animation.state.setAnimation(0, this.highlightAnimations[id], false);
        this.updateAnimationFrame();
        return new Contract<void>(resolve => {
            this.animation.state.addListener({
                complete: () => {
                    this.animation.state.clearListeners();
                    resolve(null);
                }
            });
        });
    }

    private gotoAndStopOnLastFrame(): void {
        this.animation.autoUpdate = false;
        this.animation.state.tracks[0].trackTime = 0;
        this.animation.update(this.animation.state.tracks[0].animationEnd);
    }

    private updateAnimationFrame(): void {
        this.animation.state.tracks[0].trackTime = 0;
        this.animation.update(0);
    }

    public showStaticFrame(reel: number) {
        this.animation.skeleton.setToSetupPose();
        this.animation.state.clearListeners();
        this.animation.state.clearTracks();
        this.animation.state.setAnimation(0, this.expandAnimations[reel - 1][0], false);
        this.animation.visible = true;
        this.gotoAndStopOnLastFrame();
    }

    public reset(): void {
        this.animation.autoUpdate = true;
        this.animation.state.clearTracks();
        this.animation.skeleton.setToSetupPose();
        this.hide();
    }

    public hide(): void {
        this.animation.visible = false;
    }
}
